import { canTransition } from './engagement.js';
/**
 * What moves an engagement through its lifecycle, other than an approval.
 *
 * The transition table in `engagement.ts` says which moves are *legal*. This says which moves
 * happen, and when they are allowed to. Before it existed nothing advanced an engagement past
 * SCOPED: a scan could run and find thirteen issues while the engagement still read "Scoped",
 * and the PLAN and REPORT gates were unreachable because nothing ever put an engagement in
 * front of them.
 *
 * It lives in `shared` because two parties have to agree on it. The server enforces it against
 * data it reads itself; the console uses the same function to disable a button with the real
 * reason. Two copies would drift, and the shape of that drift is a button that is offered and
 * then refused.
 */
/**
 * Stages in which a scan may start.
 *
 * Not before the scope gate is passed, and not while waiting at a later gate or after the work
 * is done: a scan during PLAN_PENDING_APPROVAL would be testing while an approver decides whether
 * testing should continue. Shared because the console offers the button and the server decides,
 * and until those stages became reachable the console's own list never had to mention them.
 */
export const SCANNABLE_STAGES = new Set([
    'SCOPED',
    'DISCOVERY',
    'PASSIVE',
    'ACTIVE',
    'TRIAGE',
]);
/**
 * Where starting a scan takes the engagement.
 *
 * A ZAP baseline crawls the seed URLs and then scans what it found passively, so a passive scan
 * started from SCOPED genuinely performs discovery and then passive testing — both stages
 * happen, and both are recorded. Each hop is checked against the transition table rather than
 * assumed, so a change there cannot be bypassed from here.
 *
 * An active scan moves nothing: reaching ACTIVE is what the PLAN approval does.
 */
export function stagesForScanStart(stage, kind) {
    if (kind !== 'PASSIVE')
        return [];
    const path = stage === 'SCOPED' ? ['DISCOVERY', 'PASSIVE'] : stage === 'DISCOVERY' ? ['PASSIVE'] : [];
    let from = stage;
    for (const to of path) {
        if (!canTransition(from, to))
            return [];
        from = to;
    }
    return path;
}
/**
 * Moves a person asks for directly.
 *
 * Every other transition is either automatic (starting a scan) or the outcome of a recorded
 * approval. Entering a gate is requested here; leaving one is decided by an approver.
 */
export const REQUESTABLE_TRANSITIONS = [
    /** Ask for permission to send attack payloads. Opens the Active gate. */
    { from: 'PASSIVE', to: 'PLAN_PENDING_APPROVAL' },
    /** Decide active testing is not needed, and start working through the findings. */
    { from: 'PASSIVE', to: 'TRIAGE' },
    /** Active testing is finished. */
    { from: 'ACTIVE', to: 'TRIAGE' },
    /** Every finding has been judged. Opens the Report gate. */
    { from: 'TRIAGE', to: 'REPORT_PENDING_APPROVAL' },
];
export function isRequestable(from, to) {
    return REQUESTABLE_TRANSITIONS.some((t) => t.from === from && t.to === to);
}
/** The moves a person could ask for from here, whether or not they are allowed right now. */
export function requestableFrom(stage) {
    return REQUESTABLE_TRANSITIONS.filter((t) => t.from === stage).map((t) => t.to);
}
/** Stages only an approval can put an engagement into. */
const APPROVAL_DECIDED = new Set(['SCOPED', 'ACTIVE', 'PUBLISHED']);
const inFlight = (status) => status === 'QUEUED' || status === 'RUNNING';
/**
 * Why a requested move cannot happen now. Empty means it can.
 *
 * Every reason is a sentence the person can act on, because "not allowed" is useless to whoever
 * has to fix it. All of them are returned, not just the first, so fixing one does not simply
 * reveal the next.
 */
export function transitionBlockers(from, to, evidence) {
    if (!isRequestable(from, to)) {
        return [
            APPROVAL_DECIDED.has(to)
                ? `${to} is reached by an approval, not by request`
                : `${from} cannot be moved to ${to} by request`,
        ];
    }
    const blockers = [];
    /*
     * Findings from a scan still running would arrive after triage had started, and a report could
     * be submitted without them. Wait for it.
     */
    const running = evidence.scans.some((s) => inFlight(s.status));
    if (running) {
        blockers.push('A scan is still running. Wait for it to finish.');
    }
    /*
     * Not while one is running: "no scan has completed" would only restate "a scan is running",
     * and if that scan succeeds the reason disappears anyway. It still applies once nothing is in
     * flight, which is when it can say something new — every scan so far failed.
     */
    if (from === 'PASSIVE' && !running) {
        const completed = evidence.scans.some((s) => s.kind === 'PASSIVE' && s.status === 'SUCCEEDED');
        if (!completed) {
            blockers.push('No passive scan has completed yet. Run one before moving on.');
        }
    }
    if (to === 'PLAN_PENDING_APPROVAL' && !evidence.activeScanPermitted) {
        blockers.push('The approved scope does not permit active scanning. Revise the scope, or move to triage.');
    }
    if (to === 'REPORT_PENDING_APPROVAL') {
        const unverified = evidence.findings.filter((f) => f.status === 'UNVERIFIED').length;
        if (unverified > 0) {
            blockers.push(unverified === 1
                ? '1 finding is still unverified. Every finding needs a decision before a report.'
                : `${unverified} findings are still unverified. Every finding needs a decision before a report.`);
        }
    }
    return blockers;
}
/**
 * Why a triage decision cannot be recorded now. Empty means it can.
 *
 * Triage happens in TRIAGE and nowhere else. Before it, findings are still arriving and a
 * decision could be overtaken by the next scan; after it, a report has been built from them,
 * and a decision changed beneath a report awaiting approval would make the report describe
 * evidence that no longer exists. A declined report returns the engagement to TRIAGE, which is
 * where corrections belong.
 */
export function triageBlockers(stage, current, from, to) {
    const blockers = [];
    if (stage !== 'TRIAGE') {
        blockers.push(`Findings are triaged in TRIAGE; this engagement is ${stage}.`);
    }
    if (current !== from) {
        blockers.push(`This finding is now ${current}, not ${from}. Re-read it before deciding.`);
    }
    if (from === to) {
        blockers.push(`This finding is already ${to}.`);
    }
    return blockers;
}
/** The decisions a triager can record, in the order the console offers them. */
export const TRIAGE_OUTCOMES = [
    'CONFIRMED',
    'FALSE_POSITIVE',
    'ACCEPTED_RISK',
    'FIXED',
    'UNVERIFIED',
];
//# sourceMappingURL=lifecycle.js.map